import { useCallback, useRef, useState } from "react";

import axios from "axios";

import { auth } from "@/lib/firebase";
import { optisignsAssetsApi } from "@/lib/api/optisigns-assets";
import type {
  OptisignsAssetItem,
  OptisignsFolderItem,
} from "@/lib/agent/payload-parser";

/** A reference passed back to the composer when the user picks assets. */
export interface OptisignsAssetRefSelection {
  assetId: string;
  name: string;
}

interface LoadParams {
  path?: string | null;
  fileType?: string;
  search?: string;
}

/** One cached view (folders + first asset page) keyed by path/search/filter. */
interface CachedView {
  folders: OptisignsFolderItem[];
  assets: OptisignsAssetItem[];
  cursor: string | null;
  hasMore: boolean;
}

/** Stable cache key for a view. */
function viewKey(params: LoadParams): string {
  return `${params.path ?? ""}|${params.search ?? ""}|${params.fileType ?? ""}`;
}

/** Best-effort user-facing message from an API/unknown error (no closure deps). */
function errorMessage(err: unknown): string {
  if (axios.isAxiosError(err) && typeof err.response?.data?.message === "string")
    return err.response.data.message;
  return err instanceof Error ? err.message : "Failed to load OptiSigns assets.";
}

/**
 * Data + selection state for the OptiSigns browse panel / inline card. Fetches
 * folders and assets (in parallel) from the Firebase-guarded UI endpoint.
 *
 * Navigation is backed by a per-session cache: revisiting a folder paints
 * instantly (assets rarely change mid-chat), and {@link prefetch} (called on
 * folder hover) warms a folder before it's opened so the click feels immediate.
 * A monotonic load sequence guards against out-of-order responses when the user
 * navigates quickly.
 */
export function useOptisignsAssets(projectId?: string) {
  const [folders, setFolders] = useState<OptisignsFolderItem[]>([]);
  const [assets, setAssets] = useState<OptisignsAssetItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Cursor state for the asset grid's infinite scroll. The last-loaded params are
  // kept in a ref so loadMore() refetches the SAME view (path/search/filter) with
  // the next cursor. Folders stay single-page (level-scoped → small counts).
  const assetsCursor = useRef<string | null>(null);
  const hasMoreAssets = useRef(false);
  const lastParams = useRef<LoadParams>({});
  // Synchronous re-entrancy guard for loadMore — a ref flips immediately (unlike
  // the loadingMore state), so overlapping IntersectionObserver callbacks can't
  // fire a second fetch with the same cursor (no duplicate appends).
  const inFlight = useRef(false);
  // Per-session view cache (path/search/filter → folders + first asset page).
  const cache = useRef<Map<string, CachedView>>(new Map());
  // Monotonic load id — a response only applies if it's still the latest load,
  // so a slow fetch can't clobber a faster subsequent navigation.
  const loadSeq = useRef(0);

  const load = useCallback(
    async (params: LoadParams): Promise<void> => {
      if (!projectId) return;
      lastParams.current = params;
      const key = viewKey(params);
      const seq = ++loadSeq.current;

      // Instant paint from cache — no spinner, no refetch.
      const cached = cache.current.get(key);
      if (cached) {
        setFolders(cached.folders);
        setAssets(cached.assets);
        assetsCursor.current = cached.cursor;
        hasMoreAssets.current = cached.hasMore;
        setError(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        // Embed-aware helper, NOT firebaseAuth.currentUser: inside the scio3
        // iframe the OptiDev app never signs into its own Firebase SDK, so
        // currentUser is always null there — the helper asks the parent via
        // the ON_GET_TOKEN bridge instead.
        const token = await auth.getIdToken();
        if (!token) throw new Error("You must be signed in.");
        const shared = {
          path: params.path ?? undefined,
          search: params.search || undefined,
        };
        const [foldersRes, assetsRes] = await Promise.all([
          optisignsAssetsApi.listFolders(token, projectId, shared),
          optisignsAssetsApi.listAssets(token, projectId, {
            ...shared,
            fileType: params.fileType,
          }),
        ]);
        if (seq !== loadSeq.current) return; // superseded by a newer load
        setFolders(foldersRes.items);
        setAssets(assetsRes.items);
        assetsCursor.current = assetsRes.pageInfo.endCursor;
        hasMoreAssets.current = assetsRes.pageInfo.hasNextPage;
        cache.current.set(key, {
          folders: foldersRes.items,
          assets: assetsRes.items,
          cursor: assetsRes.pageInfo.endCursor,
          hasMore: assetsRes.pageInfo.hasNextPage,
        });
      } catch (err: unknown) {
        if (seq !== loadSeq.current) return;
        setFolders([]);
        setAssets([]);
        assetsCursor.current = null;
        hasMoreAssets.current = false;
        setError(errorMessage(err));
      } finally {
        if (seq === loadSeq.current) setLoading(false);
      }
    },
    [projectId],
  );

  /**
   * Warm the cache for a view without touching the visible state — called on
   * folder hover so opening it is instant. No-op when already cached.
   */
  const prefetch = useCallback(
    async (params: LoadParams): Promise<void> => {
      if (!projectId) return;
      const key = viewKey(params);
      if (cache.current.has(key)) return;
      try {
        const token = await auth.getIdToken();
        if (!token) return;
        const shared = {
          path: params.path ?? undefined,
          search: params.search || undefined,
        };
        const [foldersRes, assetsRes] = await Promise.all([
          optisignsAssetsApi.listFolders(token, projectId, shared),
          optisignsAssetsApi.listAssets(token, projectId, {
            ...shared,
            fileType: params.fileType,
          }),
        ]);
        cache.current.set(key, {
          folders: foldersRes.items,
          assets: assetsRes.items,
          cursor: assetsRes.pageInfo.endCursor,
          hasMore: assetsRes.pageInfo.hasNextPage,
        });
      } catch {
        // Prefetch is best-effort; a real open will surface any error.
      }
    },
    [projectId],
  );

  /**
   * Fetch the next page of ASSETS (cursor) and APPEND. No-op while a page is
   * already loading or when there are no more pages — the panel's scroll
   * observer can call this freely.
   */
  const loadMore = useCallback(async (): Promise<void> => {
    if (!projectId || !hasMoreAssets.current || inFlight.current) return;
    inFlight.current = true;
    setLoadingMore(true);
    try {
      const token = await auth.getIdToken();
      if (!token) throw new Error("You must be signed in.");
      const params = lastParams.current;
      const res = await optisignsAssetsApi.listAssets(token, projectId, {
        path: params.path ?? undefined,
        search: params.search || undefined,
        fileType: params.fileType,
        cursor: assetsCursor.current ?? undefined,
      });
      setAssets((prev) => [...prev, ...res.items]);
      assetsCursor.current = res.pageInfo.endCursor;
      hasMoreAssets.current = res.pageInfo.hasNextPage;
      // Keep the view cache in sync so revisiting shows all loaded pages.
      const key = viewKey(params);
      const prev = cache.current.get(key);
      if (prev) {
        cache.current.set(key, {
          ...prev,
          assets: [...prev.assets, ...res.items],
          cursor: res.pageInfo.endCursor,
          hasMore: res.pageInfo.hasNextPage,
        });
      }
    } catch (err: unknown) {
      setError(errorMessage(err));
      hasMoreAssets.current = false; // stop retrying a broken cursor
    } finally {
      inFlight.current = false;
      setLoadingMore(false);
    }
  }, [projectId]);

  const toggle = useCallback((assetId: string): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(assetId)) next.delete(assetId);
      else next.add(assetId);
      return next;
    });
  }, []);

  const clearSelection = useCallback((): void => setSelected(new Set()), []);

  const selectedRefs: OptisignsAssetRefSelection[] = assets
    .filter((a) => selected.has(a.assetId))
    .map((a) => ({ assetId: a.assetId, name: a.name }));

  return {
    folders,
    assets,
    loading,
    loadingMore,
    error,
    selected,
    load,
    loadMore,
    prefetch,
    toggle,
    clearSelection,
    selectedRefs,
  };
}
